'use client';

import { useState } from 'react';
import { Img } from '@/shared/components/ui/Img';
import { ListingPhoneReveal } from './ListingPhoneReveal';
import { ArrowRightIcon } from '@/shared/components/icons';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { Textarea } from '@/shadcn/components/ui/textarea';
import * as yup from 'yup';
import { validateMobilePhoneE164 } from '@/shared/validation/validators/validateMobilePhoneE164';
import { useParams } from 'next/navigation';
import { useFormik } from 'formik';
import FormInput from '@/shared/components/ui/FormInput';
import { getErrorText, omitEmptyFields } from '@/shared/utils/formik-utils';
import { Button } from '@/shared/components/ui/Button';
import { cn } from '@/shadcn/lib/utils';
import { TurnstileWidget } from '@/features/cloudflare/components/TurnstileWidget';
import { sendListingContactEmail } from '@/api/portal-listings-contact';
import { ListingSellerContactResponseDto } from '@/api/generated/model';

const formSchema = yup
    .object({
        listingId: yup.string().required(),
        name: yup
            .string()
            .min(3, 'Meno musí obsahovať aspoň 3 znaky')
            .max(100, 'Meno môže obsahovať maximálne 100 znakov')
            .required('Povinné pole'),
        email: yup.string().required('Povinné pole').email('Nesprávny formát'),
        phone: yup
            .string()
            .test(
                'phone-e164',
                'Telefónne číslo nie je v platnom formáte (napr. +421 905 123 456)',
                function (value) {
                    if (!value) return true;
                    const res = validateMobilePhoneE164(value);
                    if (res.ok) return true;
                    return this.createError({ message: res.message });
                },
            )
            .optional(),
        message: yup
            .string()
            .min(5, 'Správa musí obsahovať minimálne 5 znakov')
            .required('Povinné pole'),
        turnstileToken: yup.string().min(5).required('Prosím overte, že nie ste robot'),
        consentAccepted: yup
            .boolean()
            .oneOf([true], 'Musíte súhlasiť s VOP a so spracúvaním osobných údajov')
            .required('Povinné pole'),
    })
    .strict(true);

type FormDto = yup.InferType<typeof formSchema>;

interface Props {
    sellerContact: ListingSellerContactResponseDto;
}

export function ListingContactForm(props: Props) {
    const params = useParams<{ id: string }>();
    const listingId = params?.id ?? '';

    const [errorResponse, setErrorResponse] = useState('');
    const [successResponse, setSuccessResponse] = useState('');
    const [loading, setLoading] = useState(false);

    const formik = useFormik<FormDto>({
        validationSchema: formSchema,
        initialValues: {
            listingId,
            name: '',
            phone: '',
            email: '',
            message:
                'Dobrý deň,\nmám záujem o inzerovanú nehnuteľnosť.\nProsím kontaktujte ma.\nĎakujem',
            consentAccepted: false,
            turnstileToken: '',
        },
        onSubmit: async (values) => {
            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) return;

            setLoading(true);
            setErrorResponse('');
            setSuccessResponse('');

            try {
                const payload = omitEmptyFields({
                    listingId: values.listingId,
                    name: values.name,
                    email: values.email,
                    phone: values.phone,
                    message: values.message,
                    turnstileToken: values.turnstileToken,
                });
                await sendListingContactEmail(payload);
                setSuccessResponse('Správa bola úspešne odoslaná. Čoskoro vás kontaktujeme.');
                formik.resetForm();
            } catch {
                setErrorResponse('Správu sa nepodarilo odoslať. Skúste to prosím neskôr.');
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <div
            className={cn(
                'shadow-[0_5px_20px_0_rgba(0,0,0,0.09)] rounded-[20px] p-[20px] py-[30px] flex flex-col gap-[20px]',
                'sm:p-[25px] md:p-[30px] lg:p-[20px] xl:px-[50px] xl:py-[40px]',
            )}
        >
            <div className="sm:grid sm:grid-cols-2 md:gap-[20px] xl:gap-[30px]">
                <div className="2xl:grid 2xl:grid-cols-2">
                    <div className="flex items-center gap-[15px] md:mt-[10px] 2xl:mt-0">
                        {props.sellerContact.role !== 'REAL_ESTATE_AGENCY' && (
                            <Img
                                className="rounded-full size-[45px]"
                                src={
                                    props.sellerContact.photoUrl ?? '/images/image_placeholder.png'
                                }
                            />
                        )}
                        <p className="text-[14px] font-[600] md:text-[15px]">
                            {props.sellerContact.name}
                        </p>
                    </div>
                    {props.sellerContact.phoneHint && (
                        <div
                            className={cn(
                                '[&>a]:text-[17px] [&>a]:font-[600] md:[&>a]:text-[19px] xl:pr-[15px] 2xl:pl-0 2xl:flex 2xl:justify-end 2xl:items-center',
                                props.sellerContact.role !== 'REAL_ESTATE_AGENCY' &&
                                    'pl-[58px] md:pl-[60px]',
                            )}
                        >
                            <ListingPhoneReveal
                                listingId={listingId}
                                phoneHint={props.sellerContact.phoneHint}
                            />
                        </div>
                    )}
                </div>
                {(props.sellerContact.agency?.logoUrl || props.sellerContact.agency?.branch) && (
                    <div className="mt-[30px] flex flex-col gap-[12px] sm:mt-[0] md:flex-row md:justify-between md:items-center md:gap-[20px] xl:gap-[30px]">
                        {props.sellerContact.agency?.logoUrl && (
                            <div className="flex items-center justify-between md:order-2">
                                <div>
                                    <Img
                                        className="w-[130px]"
                                        src={props.sellerContact.agency?.logoUrl}
                                        alt="logo"
                                    />
                                </div>
                            </div>
                        )}
                        {props.sellerContact.agency?.branch && (
                            <div className="md:order-1">
                                <ul className="[&>li]:text-[13px] flex flex-col gap-[1px] xl:[&>li]:text-[14px] xl:gap-[3px]">
                                    <li className="!text-[15px] font-[500] xl:!text-[16px]">
                                        {props.sellerContact?.agency?.branch?.name ?? ''}
                                    </li>
                                    <li>{`${props.sellerContact?.agency?.branch?.street ?? ''} ${props.sellerContact?.agency?.branch?.streetNumber ?? ''}`}</li>
                                    <li>
                                        {`${props.sellerContact?.agency?.branch?.zipCode ?? ''} ${props.sellerContact?.agency?.branch?.city ?? ''}`}
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <form onSubmit={formik.handleSubmit} className="flex flex-col gap-[10px] md:gap-[20px]">
                <div className="flex flex-col gap-[10px] md:grid md:grid-cols-2 md:gap-[20px] xl:gap-[30px]">
                    <div className="flex flex-col gap-[10px] md:gap-[15px]">
                        <FormInput
                            id="name"
                            placeholder="Meno a priezvisko"
                            required
                            error={getErrorText(formik, 'name')}
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        <FormInput
                            id="email"
                            placeholder="Email"
                            type="email"
                            required
                            error={getErrorText(formik, 'email')}
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        <FormInput
                            id="phone"
                            placeholder="Telefón (nepovinné)"
                            type="tel"
                            error={getErrorText(formik, 'phone')}
                            value={formik.values.phone}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                    </div>
                    <div>
                        <Textarea
                            id="message"
                            rows={5}
                            className="resize-none h-full p-[12px] text-[14px] leading-[130%]"
                            value={formik.values.message}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                    </div>
                </div>
                <div>
                    <TurnstileWidget
                        onVerify={(token) => formik.setFieldValue('turnstileToken', token)}
                    />
                    {formik.touched.turnstileToken && formik.errors.turnstileToken && (
                        <p className="mt-[4px] text-[12px] text-red-500">
                            {formik.errors.turnstileToken}
                        </p>
                    )}
                </div>
                <div className="flex flex-col gap-[10px] mt-[5px] md:grid md:grid-cols-2">
                    <div>
                        <div className="flex items-start gap-[8px]">
                            <Checkbox
                                id="consentAccepted"
                                checked={formik.values.consentAccepted}
                                onCheckedChange={(checked) =>
                                    formik.setFieldValue('consentAccepted', checked === true)
                                }
                            />
                            <label
                                htmlFor="consentAccepted"
                                className="-mt-[1px] text-[12px] leading-[125%] text-[#868686] [&>a]:underline"
                            >
                                Súhlasím so <a href="#">Všeobecnými podmienkami</a> a vyhlasujem, že
                                som sa oboznámil/a s informáciami o{' '}
                                <a href="#">spracúvaní osobných údajov</a>.
                            </label>
                        </div>
                        {formik.touched.consentAccepted && formik.errors.consentAccepted && (
                            <p className="mt-[4px] text-[12px] text-red-500">
                                {formik.errors.consentAccepted}
                            </p>
                        )}
                    </div>

                    <div className="flex justify-end">
                        <Button
                            type="submit"
                            color="primary-outlined"
                            className="min-w-[100px] !text-[13px]"
                            disabled={loading}
                        >
                            {loading ? 'Odosielam...' : 'Odoslať'}
                            {!loading && <ArrowRightIcon />}
                        </Button>
                    </div>
                </div>

                {successResponse && (
                    <p className="text-[13px] text-green-600 font-[500]">{successResponse}</p>
                )}
                {errorResponse && (
                    <p className="text-[13px] text-red-500 font-[500]">{errorResponse}</p>
                )}
            </form>
        </div>
    );
}
